import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { formatDomainCardText, isMultiAbilityCard } from '../../utils/domainCardFormatter';

/**
 * SelectionCard - Reusable card for selections with inline expandable details
 *
 * Used throughout the character builder for class, ancestry, equipment, etc.
 * Expand pattern matches DomainCardContainer (inline text swap with chevron).
 *
 * @param {string} title - Main title
 * @param {string} subtitle - Optional subtitle
 * @param {string} description - Expandable description
 * @param {Array} features - List of features {name, text}
 * @param {boolean} isSelected - Whether this card is currently selected
 * @param {boolean} isRecommended - Whether this is a recommended option (shows badge)
 * @param {Function} onClick - Click handler for selection
 * @param {boolean} showDetails - Whether to show expand button
 * @param {ReactNode} children - Custom content
 */
function SelectionCard({
  title,
  subtitle,
  description,
  features,
  isSelected,
  isRecommended,
  onClick,
  showDetails = true,
  children
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasDetails = showDetails && (
    (description && description.trim()) ||
    (features && features.length > 0)
  );

  const toggleExpand = (e) => {
    e.stopPropagation();
    if (hasDetails) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div className={cn(
      "p-4 rounded-lg cursor-pointer border-2 shadow-lg transition-all",
      "bg-gradient-to-br from-gray-900 to-black",
      "min-w-0 overflow-hidden", // Prevent overflow on mobile grids
      isSelected
        ? "border-green-500 bg-green-900/20 shadow-md shadow-green-500/20"
        : isRecommended
        ? "border-purple-500 hover:border-purple-400"
        : "border-red-700 hover:border-red-500"
    )}>
      {/* Main selection area */}
      <div onClick={onClick} className="cursor-pointer">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-bold text-heading-sm text-white">{title}</h3>
          {isRecommended && (
            <span className="flex items-center gap-1 text-xs text-purple-400 bg-purple-500/20 px-2 py-0.5 rounded-full shrink-0">
              <Sparkles className="w-3 h-3" />
              Suggested
            </span>
          )}
        </div>
        {subtitle && (
          <p className="text-small text-gray-300 mt-1">{subtitle}</p>
        )}
        {children}
      </div>

      {/* Inline expandable description row - matches DomainCardContainer pattern */}
      {hasDetails && (
        <div
          onClick={toggleExpand}
          className="flex items-start gap-1.5 mt-2 cursor-pointer hover:bg-gray-700/30 -mx-1 px-1 rounded transition-colors"
        >
          <div className="flex-grow min-w-0">
            {!isExpanded ? (
              <p className="text-gray-400 text-xs truncate">
                {description?.trim() || (features?.[0]?.name ? `${features[0].name}: ${features[0].text}` : 'Details')}
              </p>
            ) : (
              <div className="space-y-3 py-1">
                {description && description.trim() && (() => {
                  if (isMultiAbilityCard(description)) {
                    const abilities = formatDomainCardText(description);
                    return (
                      <div className="space-y-3">
                        {abilities.map((ability, idx) => (
                          <div key={idx}>
                            {ability.name && (
                              <h4 className="font-bold text-small text-white mb-1">
                                {ability.name}
                              </h4>
                            )}
                            <p className="text-small text-gray-300 whitespace-pre-wrap leading-relaxed">
                              {ability.description.join('\n')}
                            </p>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return (
                    <p className="text-small text-gray-300 whitespace-pre-wrap">{description}</p>
                  );
                })()}
                {features && features.length > 0 && (
                  <div className="space-y-2">
                    {features.map((feature, idx) => (
                      <div key={idx}>
                        <p className="font-medium text-small text-white">{feature.name}</p>
                        <p className="text-small text-gray-300 whitespace-pre-wrap mt-0.5">{feature.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Chevron on right - matches DomainCardContainer */}
          <span className="flex-shrink-0 text-purple-400 mt-0.5">
            <svg
              className={cn("w-4 h-4 transition-transform duration-200", isExpanded && "rotate-180")}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </div>
      )}
    </div>
  );
}

export default SelectionCard;
